import { baseApi, PAYMENTS_TAG, PAYMENTS_STATS_TAG, AUTH_TAG } from '@/shared';
import { paymentsAdapter, paymentStatsAdapter } from '../utils/helpers/adapter';

import type {
  TGetPayments,
  TPaymentsResponse,
  TPaymentApproveParams,
  TGetPaymentsStatsParams,
  TDeclinePaymentParams,
  TCancelPaymentParams,
  TPaymentsStats,
} from './types';

export const paymentsApi = baseApi.injectEndpoints({
  endpoints: build => ({
    getPayments: build.query<TPaymentsResponse, TGetPayments>({
      query: (params: TGetPayments) => ({
        url: '/admin/payments/list',
        params: {
          id: params.id,
          remote_id: params.remoteId || undefined,
          created_from: params.createdFrom,
          created_to: params.createdTo,
          amount_from: params.amountFrom,
          amount_to: params.amountTo,
          currency: params.currency,
          payer_id: params.payerId,
          client_transaction_id: params.clientTransactionId,
          shop_id: params.shopId,
          method_id: params.methodId || undefined,
          status: params.status,
          credentials: params.credentials,
          page_size: params.pageSize,
          page: params.page,
          ascending: params.ascending,
        },
      }),
      transformResponse: paymentsAdapter,
      providesTags: [PAYMENTS_TAG, AUTH_TAG],
    }),

    getPaymentsAwaitingApproval: build.query<number, void>({
      query: () => ({
        url: '/admin/payments/awaiting_approval'
      }),
      providesTags: [PAYMENTS_TAG, AUTH_TAG],
    }),

    approvePayment: build.mutation<void, TPaymentApproveParams>({
      query: ({ paymentId, paidAmount }) => ({
        url: `/admin/payments/approve/${paymentId}`,
        method: 'PUT',
        params: {
          paid_amount: paidAmount,
        }
      }),
      invalidatesTags: [PAYMENTS_TAG],
    }),

    declinePayment: build.mutation<void, TDeclinePaymentParams>({
      query: ({ paymentId }) => ({
        url: `/admin/payments/decline/${paymentId}`,
        method: 'PUT',
      }),
      invalidatesTags: [PAYMENTS_TAG],
    }),

    cancelPayment: build.mutation<void, TCancelPaymentParams>({
      query: ({ paymentId }) => ({
        url: `/admin/payments/cancel/${paymentId}`,
        method: 'PUT',
      }),
      invalidatesTags: [PAYMENTS_TAG],
    }),

    getPaymentsStats: build.query<TPaymentsStats, TGetPaymentsStatsParams>({
      query: (params: TGetPaymentsStatsParams) => ({
        url: '/admin/payments/stats',
        params: {
          created_from: params.createdFrom,
          created_to: params.createdTo,
          currency: params.currency,
          method_id: params.methodId,
          shop_id: params.shopId,
        },
      }),
      transformResponse: paymentStatsAdapter,
      providesTags: [PAYMENTS_STATS_TAG],
    }),

  }),
});

export const {
  useGetPaymentsQuery,
  useGetPaymentsAwaitingApprovalQuery,
  useApprovePaymentMutation,
  useDeclinePaymentMutation,
  useCancelPaymentMutation,
  useGetPaymentsStatsQuery,
} = paymentsApi;
